/**
 * auth.ts — Supabase Auth ผ่าน REST (ใช้ anon key เท่านั้น — ปลอดภัยใน browser)
 * เก็บ access_token (JWT) ไว้แนบ Authorization ไปยัง Worker API
 */
import { config } from './config.js';

const KEY = 'rrp_session';

export interface Session {
  accessToken: string;
  email: string;
  expiresAt: number; // epoch seconds
}

export function getSession(): Session | null {
  const raw = localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    if (s.expiresAt * 1000 < Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

export function signOut(): void {
  localStorage.removeItem(KEY);
}

/** สมัครสมาชิกใหม่ผ่าน Supabase Auth. คืน session ถ้า auto-confirm; ไม่งั้น needsConfirmation=true */
export async function signUp(
  email: string,
  password: string,
): Promise<{ session: Session | null; needsConfirmation: boolean }> {
  const res = await fetch(`${config.supabaseUrl}/auth/v1/signup`, {
    method: 'POST',
    headers: { apikey: config.anonKey, 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    error_description?: string;
    msg?: string;
    access_token?: string;
    expires_at?: number;
    user?: { email?: string } | null;
  };
  if (!res.ok) throw new Error(data.error_description || data.msg || 'สมัครไม่สำเร็จ');
  if (data.access_token && data.expires_at && data.user?.email) {
    const session: Session = {
      accessToken: data.access_token,
      email: data.user.email,
      expiresAt: data.expires_at,
    };
    localStorage.setItem(KEY, JSON.stringify(session));
    return { session, needsConfirmation: false };
  }
  // ไม่มี session กลับมา = ต้องยืนยันอีเมลก่อน
  return { session: null, needsConfirmation: true };
}

function emailFromJwt(token: string): string {
  try {
    const part = token.split('.')[1] ?? '';
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { email?: string };
    return payload.email ?? '';
  } catch {
    return '';
  }
}

export interface AuthHash {
  type: string; // 'recovery' | 'signup' | 'magiclink' | ...
  accessToken: string;
  expiresAt: number;
}

/**
 * อ่าน token จาก URL hash (หลังคลิกลิงก์ในอีเมล เช่น #access_token=..&type=recovery)
 * แล้วล้าง hash ออก (กัน token ค้าง/ชนกับ routing). คืน null ถ้าไม่มี token
 */
export function consumeAuthHash(): AuthHash | null {
  const h = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  if (!h.includes('access_token=')) return null;
  const p = new URLSearchParams(h);
  const accessToken = p.get('access_token');
  if (!accessToken) return null;
  const type = p.get('type') ?? '';
  const expiresAt = Number(p.get('expires_at') ?? '0') || Math.floor(Date.now() / 1000) + 3600;
  try {
    history.replaceState(null, '', location.pathname + location.search);
  } catch {
    /* ignore */
  }
  return { type, accessToken, expiresAt };
}

/** เก็บ session จาก access_token ที่ได้จากลิงก์อีเมล (auto-login หลังยืนยัน) */
export function storeSessionFromToken(accessToken: string, expiresAt: number): Session {
  const session: Session = { accessToken, email: emailFromJwt(accessToken), expiresAt };
  localStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

/** ตั้งรหัสผ่านใหม่ด้วย access_token จากลิงก์ recovery → เข้าสู่ระบบเลย */
export async function updatePassword(
  accessToken: string,
  newPassword: string,
  expiresAt: number,
): Promise<Session> {
  const res = await fetch(`${config.supabaseUrl}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      apikey: config.anonKey,
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ password: newPassword }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    email?: string;
    msg?: string;
    error_description?: string;
  };
  if (!res.ok) throw new Error(data.error_description || data.msg || 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ');
  const session: Session = {
    accessToken,
    email: data.email || emailFromJwt(accessToken),
    expiresAt,
  };
  localStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

/** ส่งลิงก์รีเซ็ตรหัสผ่านไปอีเมล (Supabase Auth recover) */
export async function resetPassword(email: string): Promise<void> {
  const res = await fetch(`${config.supabaseUrl}/auth/v1/recover`, {
    method: 'POST',
    headers: { apikey: config.anonKey, 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  // Supabase คืน 200 เสมอ (กัน email enumeration) — ถ้า error จริงค่อยแจ้ง
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { msg?: string; error_description?: string };
    throw new Error(body.error_description || body.msg || 'ส่งอีเมลไม่สำเร็จ');
  }
}

export async function signIn(email: string, password: string): Promise<Session> {
  const res = await fetch(`${config.supabaseUrl}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: config.anonKey, 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error_description?: string };
    throw new Error(body.error_description || 'เข้าสู่ระบบไม่สำเร็จ');
  }
  const data = (await res.json()) as { access_token: string; expires_at: number; user: { email: string } };
  const session: Session = {
    accessToken: data.access_token,
    email: data.user.email,
    expiresAt: data.expires_at,
  };
  localStorage.setItem(KEY, JSON.stringify(session));
  return session;
}
